import { currencyFor } from '@/lib/places'
import { DEFAULT_LOCALE, LOCALE_META, type Locale } from '@/lib/i18n/config'
import { getMessages } from '@/lib/i18n/messages'
import messages from './format.i18n'

export function formatCount(n: number, lang: Locale = DEFAULT_LOCALE): string {
  const t = getMessages(messages, lang)
  if (n >= 1_000_000) return t.millions((n / 1_000_000).toFixed(1))
  if (n >= 1_000) return t.thousands((n / 1_000).toFixed(1))
  return n.toString()
}

// Número con separador de miles del idioma (es: 1.234 · en: 1,234).
export function formatNumber(n: number, lang: Locale = DEFAULT_LOCALE): string {
  return n.toLocaleString(LOCALE_META[lang].intl)
}

// Porcentaje de part sobre total ("42 %" · "42%"); "—" si no hay total.
export function formatPercent(part: number, total: number, lang: Locale = DEFAULT_LOCALE): string {
  if (total <= 0) return '—'
  return new Intl.NumberFormat(LOCALE_META[lang].intl, { style: 'percent', maximumFractionDigits: 0 }).format(part / total)
}

// "2026-10-07" → "07/10" (es) · "Oct 7" (en), para ejes de gráficos.
export function formatChartDay(date: string, lang: Locale = DEFAULT_LOCALE): string {
  const [year, month, day] = date.split('-').map(Number)
  if (lang === 'es') return `${String(day).padStart(2, '0')}/${String(month).padStart(2, '0')}`
  return new Date(Date.UTC(year, month - 1, day)).toLocaleDateString(LOCALE_META[lang].intl, {
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  })
}

// Monto con la moneda del país del evento: "$5.000" (CLP en Chile),
// "US$20" (dólares), "15 €"... Sin decimales cuando la moneda no los usa.
export function formatMoney(amount: number, country: string | undefined, lang: Locale): string {
  try {
    return new Intl.NumberFormat(LOCALE_META[lang].intl, {
      style: 'currency',
      currency: currencyFor(country),
      maximumFractionDigits: Number.isInteger(amount) ? 0 : 2,
    }).format(amount)
  } catch {
    return `$${amount.toLocaleString(LOCALE_META[lang].intl)}`
  }
}

// Decimales que usa la moneda: CLP 0, USD/EUR/MXN/ARS 2.
export function currencyDecimals(currency: string): number {
  try {
    return new Intl.NumberFormat('en', { style: 'currency', currency }).resolvedOptions().maximumFractionDigits ?? 0
  } catch {
    return 0
  }
}

function separators(lang: Locale): { group: string; decimal: string } {
  const parts = new Intl.NumberFormat(LOCALE_META[lang].intl).formatToParts(1234.5)
  return {
    group: parts.find((p) => p.type === 'group')?.value ?? '.',
    decimal: parts.find((p) => p.type === 'decimal')?.value ?? ',',
  }
}

// Lo que escribe la persona en el campo de precio ("1.234,5" en es,
// "1,234.5" en en) → valor canónico con punto decimal ("1234.5"). Los
// decimales se aceptan solo si la moneda los usa (hasta 2); en CLP se ignoran.
export function parsePriceInput(value: string, currency: string, lang: Locale): string {
  const { decimal } = separators(lang)
  const decimals = currencyDecimals(currency)
  const sepIndex = decimals > 0 ? value.lastIndexOf(decimal) : -1
  const intPart = (sepIndex === -1 ? value : value.slice(0, sepIndex)).replace(/\D/g, '').replace(/^0+(?=\d)/, '')
  if (sepIndex === -1) return intPart || '0'
  const fracPart = value.slice(sepIndex + 1).replace(/\D/g, '').slice(0, decimals)
  return `${intPart || '0'}.${fracPart}`
}

// Valor canónico → texto del campo, con separador de miles del idioma. Mantiene
// un separador decimal recién escrito ("12,") para no borrarlo mientras se tipea.
export function formatPriceInput(raw: string, lang: Locale): string {
  const [intPart, fracPart] = raw.split('.')
  const n = parseInt(intPart, 10)
  if (isNaN(n)) return ''
  const int = n.toLocaleString(LOCALE_META[lang].intl, { maximumFractionDigits: 0 })
  return fracPart === undefined ? int : `${int}${separators(lang).decimal}${fracPart}`
}

// Precio a enviar: redondeado a los decimales de la moneda (si el país cambió
// de USD a CLP, "12.5" pasa a 13).
export function priceToNumber(raw: string, currency: string): number {
  const factor = 10 ** currencyDecimals(currency)
  return Math.round((Number(raw) || 0) * factor) / factor
}

// Fecha de hoy en el huso horario del navegador (no UTC — `toISOString()`
// adelantaría el día durante la noche para usuarios en husos negativos como
// Chile, el mismo bug que ya corregimos del lado del servidor).
export function todayLocal(): string {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

// Duración corta para métricas (tiempo medio de visita): "2 min 5s" / "40s".
export function formatDuration(ms: number): string {
  const totalSeconds = Math.round(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return minutes > 0 ? `${minutes} min ${seconds}s` : `${seconds}s`;
}

// Tiempo de una partida tipo cronómetro: "4:07" · "1:02:15" (con horas solo
// si pasa de una).
export function formatClock(ms: number): string {
  const total = Math.max(0, Math.round(ms / 1000))
  const h = Math.floor(total / 3600)
  const m = Math.floor((total % 3600) / 60)
  const s = String(total % 60).padStart(2, '0')
  return h > 0 ? `${h}:${String(m).padStart(2, '0')}:${s}` : `${m}:${s}`
}
